import { directSupabase as supabase } from './supabaseClient';
import { newsletterNameKey, withUnsubscribeWidget } from './newsletters.model';

export type Newsletter = {
  id: string;
  name: string;
  subject: string;
  body: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
};

export type NewsletterInput = {
  name: string;
  subject: string;
  body: string;
};

function errorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  const row = error as { message?: string; details?: string; hint?: string; code?: string };
  if (row.code === '23505') return 'A newsletter with that name already exists.';
  return row.message || row.details || row.hint || fallback;
}

async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

export const newsletters = {
  list: async (): Promise<{ data: Newsletter[] | null; error: string | null }> => {
    const { data, error } = await supabase
      .from('newsletters')
      .select('id, name, subject, body, created_at, updated_at, created_by')
      .order('name', { ascending: true });

    if (error) return { data: null, error: errorMessage(error, 'Failed to load newsletters') };
    return { data: (data || []) as Newsletter[], error: null };
  },

  create: async (input: NewsletterInput): Promise<{ data: Newsletter | null; error: string | null }> => {
    const name = input.name.trim();
    if (!name) return { data: null, error: 'Enter a newsletter name.' };

    const { data, error } = await supabase
      .from('newsletters')
      .insert({
        name,
        subject: input.subject.trim() || name,
        body: withUnsubscribeWidget(input.body),
        created_by: await currentUserId(),
      })
      .select('id, name, subject, body, created_at, updated_at, created_by')
      .single();

    if (error) return { data: null, error: errorMessage(error, 'Failed to create newsletter') };
    return { data: data as Newsletter, error: null };
  },

  update: async (id: string, input: NewsletterInput): Promise<{ data: Newsletter | null; error: string | null }> => {
    const name = input.name.trim();
    if (!name) return { data: null, error: 'Enter a newsletter name.' };

    const { data, error } = await supabase
      .from('newsletters')
      .update({
        name,
        subject: input.subject.trim() || name,
        body: input.body,
      })
      .eq('id', id)
      .select('id, name, subject, body, created_at, updated_at, created_by')
      .single();

    if (error) return { data: null, error: errorMessage(error, 'Failed to save newsletter') };
    return { data: data as Newsletter, error: null };
  },

  remove: async (id: string): Promise<{ error: string | null }> => {
    const { error } = await supabase.from('newsletters').delete().eq('id', id);
    if (error) return { error: errorMessage(error, 'Failed to delete newsletter') };
    return { error: null };
  },

  nameTaken: (items: Newsletter[], name: string, exceptId?: string | null): boolean => {
    const key = newsletterNameKey(name);
    return items.some((item) => item.id !== exceptId && newsletterNameKey(item.name) === key);
  },
};
